import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Upload, Save, Image as ImageIcon, Loader2, X, Trash2 } from 'lucide-react';

const SLIDE_KEYS = ['hero_slide_1', 'hero_slide_2', 'hero_slide_3'];

type SlideState = { file: File | null; preview: string; existing: string; id: string | null };

const AdminHeroSettings = () => {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [slides, setSlides] = useState<SlideState[]>([
    { file: null, preview: '', existing: '', id: null },
    { file: null, preview: '', existing: '', id: null },
    { file: null, preview: '', existing: '', id: null },
  ]);

  const { data: heroSettings, isLoading } = useQuery({
    queryKey: ['admin', 'hero-slides'],
    queryFn: async () => {
      const { data, error } = await supabase.from('homepage_settings').select('*').in('key', SLIDE_KEYS);
      if (error) throw error;
      return data || [];
    },
  });

  useEffect(() => {
    if (!heroSettings) return;
    setSlides(prev => prev.map((s, i) => {
      const setting = heroSettings.find(h => h.key === SLIDE_KEYS[i]);
      return setting?.image_url
        ? { ...s, existing: setting.image_url, preview: setting.image_url, id: setting.id }
        : { ...s, existing: '', preview: '', id: null };
    }));
  }, [heroSettings]);

  const handleFileChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Use JPG, PNG, or WebP.', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 5MB.', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setSlides(prev => prev.map((s, i) => i === index ? { ...s, file, preview: reader.result as string } : s));
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const removeSlide = async (index: number) => {
    const slide = slides[index];
    if (slide.id) {
      await supabase.from('homepage_settings').delete().eq('id', slide.id);
      qc.invalidateQueries({ queryKey: ['admin', 'hero-slides'] });
      qc.invalidateQueries({ queryKey: ['hero-slides'] });
    }
    setSlides(prev => prev.map((s, i) => i === index ? { file: null, preview: '', existing: '', id: null } : s));
    toast({ title: `Slide ${index + 1} removed` });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      setUploading(true);
      try {
        for (let i = 0; i < 3; i++) {
          const slide = slides[i];
          if (!slide.file) continue;

          const ext = slide.file.name.split('.').pop();
          const path = `hero-images/slide-${i + 1}-${Date.now()}.${ext}`;
          const { error: upErr } = await supabase.storage.from('homepage').upload(path, slide.file, { cacheControl: '3600', upsert: false });
          if (upErr) throw upErr;

          const { data: { publicUrl } } = supabase.storage.from('homepage').getPublicUrl(path);

          if (slide.id) {
            await supabase.from('homepage_settings').update({ image_url: publicUrl, value: publicUrl, updated_at: new Date().toISOString() }).eq('id', slide.id);
          } else {
            await supabase.from('homepage_settings').insert({ key: SLIDE_KEYS[i], image_url: publicUrl, value: publicUrl });
          }
        }
      } finally {
        setUploading(false);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'hero-slides'] });
      qc.invalidateQueries({ queryKey: ['hero-slides'] });
      setSlides(prev => prev.map(s => ({ ...s, file: null })));
      toast({ title: 'Hero slides saved!', description: 'Homepage will now show your slides.' });
    },
    onError: (e: any) => {
      setUploading(false);
      toast({ title: 'Error saving', description: e.message, variant: 'destructive' });
    },
  });

  const hasNewFiles = slides.some(s => s.file !== null);

  if (isLoading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold mb-2">Hero Section Images</h1>
      <p className="text-muted-foreground mb-6">
        Upload up to 3 images for the homepage hero slider. They auto-rotate every 4 seconds with a fade effect.
      </p>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {slides.map((slide, i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <CardTitle className="font-heading text-base flex items-center justify-between">
                Slide {i + 1}
                {slide.preview && (
                  <button onClick={() => removeSlide(i)} className="text-destructive hover:text-destructive/80 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </CardTitle>
              <CardDescription className="text-xs">Recommended: 1920×800px</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {slide.preview ? (
                <div className="relative group">
                  <img src={slide.preview} alt={`Slide ${i + 1}`} className="w-full aspect-video object-cover rounded-lg border" />
                  {slide.file && (
                    <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded">New</div>
                  )}
                </div>
              ) : (
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg aspect-video flex flex-col items-center justify-center bg-muted/20">
                  <ImageIcon className="h-8 w-8 mb-2 text-muted-foreground/40" />
                  <p className="text-xs text-muted-foreground">No image</p>
                </div>
              )}

              <Label htmlFor={`slide-${i}`}>
                <Button variant="outline" size="sm" className="w-full gap-2 cursor-pointer" asChild>
                  <span><Upload className="h-3 w-3" /> {slide.preview ? 'Change Image' : 'Upload Image'}</span>
                </Button>
              </Label>
              <input id={`slide-${i}`} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" onChange={e => handleFileChange(i, e)} className="hidden" />
            </CardContent>
          </Card>
        ))}
      </div>

      <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending || uploading || !hasNewFiles} size="lg" className="gap-2">
        {saveMutation.isPending || uploading
          ? <><Loader2 className="h-4 w-4 animate-spin" />{uploading ? 'Uploading...' : 'Saving...'}</>
          : <><Save className="h-4 w-4" /> Save Slides</>}
      </Button>

      <Card className="mt-6 border-primary/20 bg-primary/5">
        <CardContent className="pt-5">
          <h3 className="font-heading font-semibold mb-2 text-sm">Tips</h3>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Recommended: 1920×800px, JPG/PNG/WebP, max 5MB each</li>
            <li>• Slides auto-rotate every 4 seconds with a fade effect</li>
            <li>• If no images are uploaded, the hero shows a plain dark background</li>
            <li>• Click the trash icon to remove a slide</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminHeroSettings;
